import type { IConfigAPI } from '@monofe/util';
import type { IAuthManagerInstantiator } from '../../controller/interface/auth';
import type { IMediaManagerInstantiator } from '../../controller/interface/media';
import type { IUserStateInstantiator } from '../../controller/interface/userstate';
import type { IWorkspaceDBInstantiator } from '../../db/interface';
import type { ISession, ISessionArgs } from '../interface/session';
import sessionDB from '../private/db';
import sessionBuilder from './session.build';
import sessionCache from './session.cache';
import sessionCallback from './session.callback';

async function open(
    configAPI: IConfigAPI,
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    name: string,
    args?: ISessionArgs,
): Promise<ISession> {
    const existing = sessionCache.get(name);

    if (existing) {
        return Promise.resolve(existing);
    }

    let sessionObj: ISession | null = null;

    if (args) {
        sessionObj = await sessionBuilder.buildFromArgs(
            configAPI,
            authInst,
            mediaInst,
            userInst,
            dbInst,
            name,
            args,
        );
    } else {
        /* Ignore throw errors */
        const sessionData = await sessionDB.load(name).catch(() => undefined);

        if (sessionData) {
            sessionObj = await sessionBuilder.buildFromSessionData(
                authInst,
                mediaInst,
                userInst,
                dbInst,
                sessionData,
            );
        }
    }

    if (!sessionObj) {
        throw new Error(`Cannot find/load session: ${name}/${JSON.stringify(args)}`);
    }

    return sessionObj;
}

async function exists(name: string): Promise<boolean> {
    /* Ignore throw errors */
    const sessionData = await sessionDB.load(name).catch(() => undefined);
    const cached = sessionCache.get(name);

    return Boolean(cached || sessionData);
}

export default Object.freeze({
    open,
    exists,
    ...sessionCallback,
});
